import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { toast } from 'sonner';
import { usePageTitle } from '@/hooks/usePageTitle';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function ContactPage() {
  const [pageData, setPageData] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', inquiry_type: 'general' });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  usePageTitle();

  useEffect(() => {
    api.get('/pages/contact').then(res => {
      setPageData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/contact', form);
      toast.success(res.data.message);
      setForm({ name: '', email: '', subject: '', message: '', inquiry_type: 'general' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sections = pageData?.sections || {};

  if (loading) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="flex justify-center py-32"><ConcentricRings size={60} opacity={0.3} /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left - Info */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.p variants={fadeUp} className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">
                Get in Touch
              </motion.p>
              <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-6 leading-[1.1]">
                {sections.headline || 'Contact & Collaborate'}
              </motion.h1>
              <motion.p variants={fadeUp} className="font-['Inter'] text-base leading-[1.7] text-[hsl(var(--muted-foreground))] mb-10">
                {sections.description}
              </motion.p>

              {sections.expectations?.length > 0 && (
                <motion.div variants={fadeUp}>
                  <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-4">
                    What to Expect
                  </p>
                  <ul className="space-y-3">
                    {sections.expectations.map((exp, i) => (
                      <li key={exp.slice(0,20)} className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))] pl-4 border-l border-[rgba(201,168,76,0.2)]">
                        {exp}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* Decorative */}
              <div className="mt-12 hidden lg:block">
                <ConcentricRings size={120} opacity={0.1} />
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                      Your Name
                    </label>
                    <Input
                      value={form.name}
                      onChange={e => setForm({...form, name: e.target.value})}
                      className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm({...form, email: e.target.value})}
                      className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                      data-testid="contact-email-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                      Inquiry Type
                    </label>
                    <Select value={form.inquiry_type} onValueChange={(v) => setForm({...form, inquiry_type: v})}>
                      <SelectTrigger className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)]">
                        <SelectItem value="general" className="text-[hsl(var(--foreground))] font-['Inter'] text-sm">General Inquiry</SelectItem>
                        <SelectItem value="collaboration" className="text-[hsl(var(--foreground))] font-['Inter'] text-sm">Collaboration</SelectItem>
                        <SelectItem value="speaking" className="text-[hsl(var(--foreground))] font-['Inter'] text-sm">Speaking Engagement</SelectItem>
                        <SelectItem value="media" className="text-[hsl(var(--foreground))] font-['Inter'] text-sm">Media & Press</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                      Subject
                    </label>
                    <Input
                      value={form.subject}
                      onChange={e => setForm({...form, subject: e.target.value})}
                      className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                      Message
                    </label>
                    <Textarea
                      value={form.message}
                      onChange={e => setForm({...form, message: e.target.value})}
                      rows={5}
                      className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm resize-none"
                      data-testid="contact-message-input"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-6 rounded-lg hover:brightness-[1.03] disabled:opacity-50"
                    data-testid="contact-form-submit-button"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
